import React, { useState, useEffect } from 'react';
import facilityService from '../services/facilityService';
import './FacilityForm.css';

const STATUS_LABELS = {
  processing: 'Wird gekoppelt…',
  paired: 'Gekoppelt',
  expired: 'Abgelaufen'
};

const FacilityForm = ({ facility, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    active: true,
    utility_allowance: 50.0,
    sumup_reader_id: '',
    invoice_prefix: ''
  });
  const [errors, setErrors] = useState({});
  const [pairingCode, setPairingCode] = useState('');
  const [pairingStatus, setPairingStatus] = useState(null);
  const [pairingError, setPairingError] = useState('');
  const [pairing, setPairing] = useState(false);

  useEffect(() => {
    if (facility) {
      setFormData({
        name: facility.name || '',
        active: facility.active !== undefined ? facility.active : true,
        utility_allowance: facility.utility_allowance !== undefined ? facility.utility_allowance : 50.0,
        sumup_reader_id: facility.sumup_reader_id || '',
        invoice_prefix: facility.invoice_prefix || ''
      });
    }
  }, [facility]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }
    
    if (formData.utility_allowance < 0) {
      newErrors.utility_allowance = 'Pauschale muss >= 0 sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handlePairReader = async () => {
    setPairing(true);
    setPairingError('');
    try {
      const result = await facilityService.pairReader(facility.id, {
        pairing_code: pairingCode,
        name: formData.name
      });
      setFormData(prev => ({ ...prev, sumup_reader_id: result.reader_id }));
      setPairingStatus({ status: result.status, device_model: result.device_model });
      setPairingCode('');
    } catch (err) {
      if (err.response?.status === 409) {
        setPairingError('Code abgelaufen, neuen Code am Gerät erzeugen');
      } else {
        setPairingError(err.response?.data?.detail || 'Kopplung fehlgeschlagen');
      }
    } finally {
      setPairing(false);
    }
  };

  const handleCheckStatus = async () => {
    setPairingError('');
    try {
      const result = await facilityService.getReaderStatus(facility.id);
      setPairingStatus({ status: result.status, device_model: result.device_model });
    } catch (err) {
      setPairingError(err.response?.data?.detail || 'Status konnte nicht abgerufen werden');
    }
  };

  const renderStatusBadge = () => {
    const label = STATUS_LABELS[pairingStatus.status] || 'Unbekannt';
    const variant = STATUS_LABELS[pairingStatus.status] ? pairingStatus.status : 'unknown';
    return (
      <span className={`status-badge status-${variant}`}>
        {label}{pairingStatus.device_model ? ` · ${pairingStatus.device_model}` : ''}
      </span>
    );
  };

  return (
    <div className="facility-form-overlay">
      <div className="facility-form">
        <h2>{facility ? 'Anlage bearbeiten' : 'Neue Anlage'}</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'error' : ''}
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="utility_allowance">Strom+Wasser-Pauschale (€)</label>
            <input
              type="number"
              id="utility_allowance"
              name="utility_allowance"
              value={formData.utility_allowance}
              onChange={handleChange}
              min="0"
              step="0.01"
              className={errors.utility_allowance ? 'error' : ''}
            />
            {errors.utility_allowance && <span className="error-text">{errors.utility_allowance}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="sumup_reader_id">SumUp Reader ID</label>
            <input
              type="text"
              id="sumup_reader_id"
              name="sumup_reader_id"
              value={formData.sumup_reader_id}
              onChange={handleChange}
              placeholder="z.B. rdr_5FHCPF39489A68WF3QE2ZBPJWF"
              className={errors.sumup_reader_id ? 'error' : ''}
            />
            {errors.sumup_reader_id && <span className="error-text">{errors.sumup_reader_id}</span>}
            <small className="help-text">Optional: Reader-ID für Kartenzahlungen an dieser Anlage</small>
          </div>

          {facility && facility.id && (
            <div className="form-group pairing-section">
              <label htmlFor="pairingCode">Reader koppeln</label>
              <div className="pairing-row">
                <input
                  type="text"
                  id="pairingCode"
                  value={pairingCode}
                  onChange={(e) => setPairingCode(e.target.value)}
                  placeholder="8–9 Zeichen vom Reader-Display"
                  aria-label="Pairing-Code vom Gerät"
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handlePairReader}
                  disabled={!pairingCode || pairing}
                >
                  {pairing ? 'Koppeln…' : 'Koppeln'}
                </button>
              </div>
              {(formData.sumup_reader_id || pairingStatus) && (
                <div className="pairing-status-row">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCheckStatus}
                  >
                    Status prüfen
                  </button>
                  {pairingStatus && renderStatusBadge()}
                </div>
              )}
              {pairingError && <span className="error-text pairing-error">{pairingError}</span>}
              <small className="help-text">Pairing-Code am Reader-Display erzeugen und hier eingeben</small>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="invoice_prefix">Rechnungspräfix</label>
            <input
              type="text"
              id="invoice_prefix"
              name="invoice_prefix"
              value={formData.invoice_prefix}
              onChange={handleChange}
              placeholder="z.B. mm1"
              maxLength="20"
            />
            <small className="help-text">Kürzel für diese Anlage in Rechnungsnummern</small>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
              />
              Anlage ist aktiv
            </label>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn btn-secondary">
              Abbrechen
            </button>
            <button type="submit" className="btn btn-primary">
              {facility ? 'Speichern' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FacilityForm;
